import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { sendSubmission } from "@/lib/api";

const inputCls =
    "w-full bg-paper border-2 border-ink px-4 py-3 text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-pink transition-shadow duration-150";

export default function SubmissionForm({
    idPrefix = "question-form",
    lockKind = null,
    heading,
    blurb,
}) {
    const [kind, setKind] = useState(lockKind || "question");
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [sending, setSending] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.message.trim()) {
            toast.error("Please add your name and a message.");
            return;
        }
        setSending(true);
        try {
            await sendSubmission({ ...form, kind });
            toast.success(
                kind === "press"
                    ? "Inquiry sent. We'll be in touch soon."
                    : "Received loud and clear. Thank you for writing in!"
            );
            setForm({ name: "", email: "", message: "" });
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            data-testid={`${idPrefix}-form`}
            className="bg-cream border-2 border-ink hard-shadow p-6 sm:p-8"
        >
            {heading && (
                <h3 className="font-display font-black text-2xl text-ink">{heading}</h3>
            )}
            {blurb && <p className="mt-2 text-sm text-ink/70">{blurb}</p>}

            {!lockKind && (
                <div className="mt-6 flex gap-3" role="radiogroup" aria-label="Type">
                    {[
                        { v: "question", label: "Ask a question" },
                        { v: "story", label: "Share a story" },
                    ].map((o) => (
                        <button
                            key={o.v}
                            type="button"
                            data-testid={`${idPrefix}-kind-${o.v}`}
                            onClick={() => setKind(o.v)}
                            className={`font-sub font-bold text-sm px-4 py-2.5 border-2 border-ink transition-colors duration-150 ${
                                kind === o.v
                                    ? "bg-pink text-cream hard-shadow-sm"
                                    : "bg-paper text-ink hover:bg-mustard/30"
                            }`}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
            )}

            <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <input
                    data-testid={`${idPrefix}-name-input`}
                    className={inputCls}
                    placeholder="Your name *"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <input
                    data-testid={`${idPrefix}-email-input`}
                    type="email"
                    className={inputCls}
                    placeholder="Email (optional)"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
            </div>
            <textarea
                data-testid={`${idPrefix}-message-input`}
                className={`${inputCls} mt-4 min-h-[140px] resize-y`}
                placeholder={
                    kind === "story"
                        ? "Tell us your story, as much or as little as you like…"
                        : kind === "press"
                          ? "Your inquiry: outlet, deadline, what you need…"
                          : "Drop your question for an upcoming episode…"
                }
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
            <button
                type="submit"
                data-testid={`${idPrefix}-submit-btn`}
                disabled={sending}
                className="mt-6 inline-flex items-center gap-2 bg-ink text-paper font-sub font-bold px-7 py-3.5 border-2 border-ink hard-shadow-pink transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
            >
                <Send className="w-4 h-4" />
                {sending ? "Sending…" : "Send it over"}
            </button>
        </form>
    );
}
